import { APIConfiguration, Service, useGet, useGetWithCount, useList, useRead } from "@api/common";
import type { Integration, IntegrationJob, ListParams, ReadParams } from "./types";

export type { Integration };

export interface ZenotiCenter {
  id: string;
  code: string;
  name: string;
  displayName: string | null;
  /** IANA time zone, or null when Zenoti reports a zone Helios does not map. */
  timeZone: string | null;
}

export class IntegrationService extends Service<Integration> {
  constructor() {
    super("/integrations");
  }

  /** Queues the provider catalog and schedule ingest jobs immediately. */
  async sync(id: string): Promise<IntegrationJob[]> {
    return this.post<IntegrationJob[]>(`${this.basePath}/${id}/sync`);
  }

  async fitdegreeCreate(body: { gym: string; apiKey: string }): Promise<any> {
    const response = await this.post(`${this.basePath}/fitdegree`, body);
    return response;
  }

  async fitdegreeUpdate(id: string, body: { apiKey: string }): Promise<any> {
    const response = await this.put(`${this.basePath}/${id}/fitdegree`, body);
    return response;
  }

  async fitdegreeActivate(id: string, body: { spotIds: string[] }): Promise<any> {
    const response = await this.post(`${this.basePath}/${id}/fitdegree/activate`, body);
    return response;
  }

  async fitdegreeUpdateOptions(id: string, body: { spotIds: string[] }): Promise<any> {
    const response = await this.put(`${this.basePath}/${id}/fitdegree/options`, body);
    return response;
  }

  async mindbodyCreate(body: { siteId: string; gym: string }): Promise<any> {
    const response = await this.post(`${this.basePath}/mindbody`, body);
    return response;
  }

  async mindbodyCreateActivationCode(body: { siteId: string; gym: string }): Promise<any> {
    const response = await this.post(`${this.basePath}/mindbody/activation-code`, body);
    return response;
  }

  async mindbodyLocations(id: string): Promise<Array<{ Id: number; Name: string }>> {
    return this.get(`${this.basePath}/${id}/mindbody/locations`);
  }

  async mindbodyUpdateOptions(
    id: string,
    body: {
      locationIds?: string[];
      productOverrides?: Record<string, string>;
    },
  ): Promise<any> {
    const response = await this.put(`${this.basePath}/${id}/mindbody/options`, body);
    return response;
  }

  /** Validates an organization key and lists its centers. Persists nothing. */
  async zenotiCenters(body: { gym: string; orgApiKey: string }): Promise<ZenotiCenter[]> {
    return this.post<ZenotiCenter[]>(`${this.basePath}/zenoti/centers`, body);
  }

  async zenotiCreate(body: {
    gym: string;
    orgApiKey: string;
    centerId: string;
  }): Promise<Integration> {
    return this.post<Integration>(`${this.basePath}/zenoti`, body);
  }

  async tablelistCreate(body: { gym: string }): Promise<any> {
    const response = await this.post(`${this.basePath}/tablelist`, body);
    return response;
  }

  async gymbleCreate(body: { gym: string; apiKey: string }): Promise<any> {
    const response = await this.post(`${this.basePath}/gymble`, body);
    return response;
  }

  async activeCampaignCreate(body: { gym: string }): Promise<any> {
    const response = await this.post(`${this.basePath}/active-campaign`, body);
    return response;
  }

  async webhookCreate(body: { gym: string }): Promise<any> {
    const response = await this.post(`${this.basePath}/webhook`, body);
    return response;
  }

  async marianaTekCreate(body: {
    gym: string;
    code: string;
    subdomain: string;
    active: boolean;
  }): Promise<any> {
    const response = await this.post(`${this.basePath}/mariana-tek`, {
      ...body,
      redirect: `${window.location.origin}/oauth/mariana-tek`,
    });
    return response;
  }

  async marianaTekUpdate(id: string, body: { locations: string[] }): Promise<any> {
    const response = await this.put(`${this.basePath}/${id}/mariana-tek/options`, body);
    return response;
  }

  async marianaTekActivate(id: string): Promise<any> {
    const response = await this.post(`${this.basePath}/${id}/mariana-tek/activate`);
    return response;
  }

  async marianaTekOAuth(body: { gym: string; subdomain: string }): Promise<any> {
    const response = await this.post(`${this.basePath}/mariana-tek/oauth`, {
      ...body,
      redirect: `${window.location.origin}/oauth/mariana-tek`,
      state: {
        subdomain: body.subdomain,
      },
    });
    return response;
  }

  async squareCreate(body: { gym: string; code: string }): Promise<any> {
    const response = await this.post(`${this.basePath}/square`, body);
    return response;
  }

  async squareOAuth(body: { gym: string }): Promise<any> {
    const response = await this.post(`${this.basePath}/square/oauth`, {
      ...body,
      state: {},
    });
    return response;
  }

  async hubspotCreate(body: { gym: string; code: string; redirect: string }): Promise<any> {
    const response = await this.post(`${this.basePath}/hubspot`, body);
    return response;
  }

  async hubspotOAuth(body: { gym: string; redirect: string }): Promise<any> {
    const response = await this.post(`${this.basePath}/hubspot/oauth`, {
      ...body,
    });
    return response;
  }

  async shopifyCreate(body: { gym: string; shop: string; code: string }): Promise<any> {
    const response = await this.post(`${this.basePath}/shopify`, body);
    return response;
  }

  async shopifyOAuth(body: { gym: string; shop: string }): Promise<{ url: string }> {
    const response = await this.post(`${this.basePath}/shopify/oauth`, body);
    return response as any;
  }
}

export const integrationService = new IntegrationService();

export function useIntegrations(params?: ListParams<Integration>, config?: APIConfiguration) {
  return useList(integrationService, params, config);
}

export function useIntegration(id: string, params?: ReadParams, config?: APIConfiguration) {
  return useRead(integrationService, id, params, config);
}

/** Same as `useIntegrationJobs`, plus the `x-helios-meta-count` total. */
export function useIntegrationJobsWithCount(
  id?: string,
  params?: ListParams<IntegrationJob>,
  config?: APIConfiguration,
) {
  return useGetWithCount<IntegrationJob[]>(
    integrationService,
    "/integration-jobs",
    {
      ...params,
      integration: id,
    },
    {
      ...config,
      continue: () => !!id,
    },
  );
}

export function useIntegrationJobs(
  id?: string,
  params?: ListParams<IntegrationJob>,
  config?: APIConfiguration,
) {
  return useGet<IntegrationJob[]>(
    integrationService,
    "/integration-jobs",
    {
      ...params,
      integration: id,
    },
    {
      ...config,
      continue: () => !!id,
    },
  );
}
